import { useRef, useEffect } from 'react';
import { useAuthStore } from "@/store/authStore";
import { trpc } from "@/trpc/client";
import { Navigation, Clock, CheckCircle, Signal, Wifi, WifiOff, Loader2, MapPin, Zap } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { useSocket } from "@/hooks/useSocket";
import TransporterLocationSender from "../../components/TransporterLocationSender";
import SOSButton from "@/components/sos/SOSButton";

const DriverDashboard = () => {
    const { user } = useAuthStore();
    const utils = trpc.useContext();
    const { isConnected } = useSocket();

    // Fetch assignments
    const { data: assignments } = trpc.booking.getMyActiveAssignments.useQuery();

    // Mutations
    const updateStatus = trpc.booking.updateAssignmentStatus.useMutation({
        onSuccess: (data) => {
            toast.success("Status updated");
            utils.booking.getMyActiveAssignments.invalidate();
        }
    });

    const activeAssignment = assignments?.[0]; // Show first active one

    const handleStartTrip = () => {
        if (!activeAssignment) return;
        updateStatus.mutate({ assignmentId: activeAssignment.id, status: 'IN_TRANSIT' });
    };

    const handleCompleteTrip = () => {
        if (!activeAssignment) return;
        updateStatus.mutate({ assignmentId: activeAssignment.id, status: 'COMPLETED' });
    };

    return (
        <div className="container mx-auto p-6 space-y-8 animate-fade-in relative">

            {/* Consistent Tracking Logic */}
            <TransporterLocationSender
                jobId={activeAssignment?.booking.jobId || ''}
                active={activeAssignment?.status === 'IN_TRANSIT'}
                showStatus={true}
            />

            {/* SOS EMERGENCY BUTTON */}
            {activeAssignment && activeAssignment.status === 'IN_TRANSIT' && (
                <div className="fixed bottom-4 right-4 z-50 md:bottom-8 md:right-8">
                    <SOSButton
                        jobId={activeAssignment.booking.job.id}
                        onSuccess={() => toast.error("SOS Signal Broadcasted to HQ!")}
                    />
                </div>
            )}

            {/* Header */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <div>
                    <h1 className="text-2xl md:text-3xl font-bold bg-gradient-to-r from-primary to-primary-glow bg-clip-text text-transparent">
                        Mission Control
                    </h1>
                    <p className="text-muted-foreground text-sm md:text-base">Welcome back, {user?.profile?.fullName || 'Pilot'}. Safe driving.</p>
                </div>

                {/* Status Badges */}
                <div className="flex w-full md:w-auto items-center gap-3 overflow-x-auto">
                    <Badge variant={isConnected ? "outline" : "destructive"} className={`h-9 px-4 gap-2 whitespace-nowrap ${isConnected ? "border-green-500 text-green-500 bg-green-500/10" : ""}`}>
                        {isConnected ? <Wifi className="w-4 h-4" /> : <WifiOff className="w-4 h-4" />}
                        {isConnected ? "Connected" : "Reconnecting..."}
                    </Badge>
                </div>
            </div>

            {/* Current Mission Card */}
            <Card className="border-primary/50 shadow-lg shadow-primary/10">
                <CardHeader>
                    <CardTitle className="flex items-center gap-2 justify-between">
                        <div className="flex items-center gap-2">
                            <Navigation className="w-5 h-5 text-primary" />
                            Current Assignment
                        </div>
                        {activeAssignment && <Badge variant={activeAssignment.status === 'IN_TRANSIT' ? 'default' : 'secondary'}>{activeAssignment.status}</Badge>}
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    {activeAssignment ? (
                        <div className="space-y-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className="space-y-1">
                                    <p className="text-xs text-muted-foreground uppercase tracking-wider">Origin</p>
                                    <p className="font-semibold text-lg">{activeAssignment.booking.job.pickupDistrict}</p>
                                    <p className="text-sm text-muted-foreground truncate">{activeAssignment.booking.job.pickupAddress}</p>
                                </div>
                                <div className="space-y-1">
                                    <p className="text-xs text-muted-foreground uppercase tracking-wider">Destination</p>
                                    <p className="font-semibold text-lg">{activeAssignment.booking.job.deliveryDistrict}</p>
                                    <p className="text-sm text-muted-foreground truncate">{activeAssignment.booking.job.deliveryAddress}</p>
                                </div>
                            </div>

                            <div className="bg-muted/30 p-4 rounded-md flex items-center justify-between">
                                <div>
                                    <p className="text-xs text-muted-foreground">Vehicle</p>
                                    <p className="font-medium">{activeAssignment.vehicle?.make} {activeAssignment.vehicle?.model}</p>
                                    <p className="text-xs text-muted-foreground">{activeAssignment.vehicle?.licensePlate}</p>
                                </div>
                                <div className="text-right">
                                    <p className="text-xs text-muted-foreground">Payload</p>
                                    <p className="font-medium">{activeAssignment.assignedLoadCount} Loads</p>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="text-center py-12">
                            <div className="mx-auto w-12 h-12 bg-muted rounded-full flex items-center justify-center mb-4">
                                <Clock className="w-6 h-6 text-muted-foreground" />
                            </div>
                            <h3 className="text-lg font-semibold">No Active Assignments</h3>
                            <p className="text-muted-foreground">Stand by for dispatch from your Fleet Manager.</p>
                        </div>
                    )}
                </CardContent>
                {activeAssignment && (
                    <CardFooter className="bg-muted/10 p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                        {activeAssignment.status === 'ACCEPTED' && (
                            <Button
                                className="w-full md:col-span-2 h-14 text-lg gap-2 shadow-lg hover:shadow-xl transition-all"
                                onClick={handleStartTrip}
                                disabled={updateStatus.isLoading}
                            >
                                {updateStatus.isLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Navigation className="w-5 h-5" />}
                                {updateStatus.isLoading ? 'Starting Trip...' : 'Start Trip'}
                            </Button>
                        )}
                        {activeAssignment.status === 'IN_TRANSIT' && (
                            <>
                                <div className="w-full flex items-center justify-center p-2 bg-blue-500/10 rounded-md border border-blue-500/20 text-blue-500 h-14 md:col-span-1">
                                    <div className="flex items-center gap-2 text-sm font-medium">
                                        <Signal className="w-4 h-4 animate-pulse" />
                                        <span>Tracking Enforced by System</span>
                                    </div>
                                </div>

                                <Button
                                    className="w-full h-14 text-base md:text-lg gap-2 bg-green-600 hover:bg-green-700 shadow-lg hover:shadow-xl transition-all"
                                    onClick={handleCompleteTrip}
                                    disabled={updateStatus.isLoading}
                                >
                                    {updateStatus.isLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : <CheckCircle className="w-5 h-5" />}
                                    {updateStatus.isLoading ? 'Completing...' : 'Complete Drop-off'}
                                </Button>
                            </>
                        )}
                    </CardFooter>
                )}
            </Card>

            {/* Past Missions (Placeholder for "Whole Process" feel) */}
            <div className="space-y-4 opacity-50 pointer-events-none">
                <h3 className="text-lg font-semibold text-muted-foreground">Trip History</h3>
                <Card>
                    <CardHeader><CardTitle className="text-base text-muted-foreground">History is empty</CardTitle></CardHeader>
                </Card>
            </div>
        </div>
    );
};

export default DriverDashboard;

